import React from 'react';

// Shared sidebar/nav-tile icon set — one glyph per concept, reused across every
// app instead of each app keeping its own copy (list/grid/person/company/
// timeline/document previously existed 2-4x over, duplicated per app). Same
// two-path SVG convention as every other nav icon in the repo: a light-grey
// stroke-only outline (always visible) plus a `.fill` path (a closed, fillable
// shape) that the global `.nav-link`/`.large-nav-link` CSS shows solid on the
// active/selected state. Every outline/fill shape here is built from <path>
// elements only (never a bare <circle>/<rect>) — a stray non-path outline
// element is exempt from that CSS toggle and stays visible permanently, which
// is the exact bug this set was designed to avoid (see network's old
// IconNetwork.js person glyph).
export const IconLibrary = ({ name }) => {
  if (!name) return null

  name = name.replace(/ /g, '-').toLowerCase()

  const icons = {
    list: list(),
    grid: grid(),
    person: person(),
    company: company(),
    timeline: timeline(),
    document: document_(),
    published: published(),
    calendar: calendar(),
  }

  return icons[name] || null
}

const list = () => (
  <svg className="icon icon-list" width="100%" height="100%" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet">
    <path d="M4,7h16M4,12h16M4,17h16" style={{ fill: 'none', stroke: 'rgb(224,218,212)', strokeWidth: '1.7px' }} />
    <path className="fill" d="M4,6h16v2h-16zM4,11h16v2h-16zM4,16h16v2h-16z" style={{ fill: '#222020', stroke: 'none' }} />
  </svg>
)

const grid = () => (
  <svg className="icon icon-grid" width="100%" height="100%" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet">
    <path d="M3,4h5v16h-5zM10,4h5v10h-5zM17,4h5v13h-5z" style={{ fill: 'none', stroke: 'rgb(224,218,212)', strokeWidth: '1.7px' }} />
    <path className="fill" d="M3,4h5v16h-5zM10,4h5v10h-5zM17,4h5v13h-5z" style={{ fill: '#222020', stroke: 'none' }} />
  </svg>
)

const person = () => (
  <svg className="icon icon-person" width="100%" height="100%" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet">
    <path d="M15.1,7.6 A3.1,3.1 0 1 1 8.9,7.6 A3.1,3.1 0 1 1 15.1,7.6 M5.2,20 C5.2,15.3 8,13.2 12,13.2 C16,13.2 18.8,15.3 18.8,20"
      style={{ fill: 'none', stroke: 'rgb(224,218,212)', strokeWidth: '1.7px' }} />
    <path className="fill" d="M15.1,7.6 A3.1,3.1 0 1 1 8.9,7.6 A3.1,3.1 0 1 1 15.1,7.6 Z M5.2,20 C5.2,15.3 8,13.2 12,13.2 C16,13.2 18.8,15.3 18.8,20 Z"
      style={{ fill: '#222020', stroke: 'none' }} />
  </svg>
)

const company = () => (
  <svg className="icon icon-company" width="100%" height="100%" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet">
    <path d="M5.4,3 H18.6 A1.4,1.4 0 0 1 20,4.4 V19.6 A1.4,1.4 0 0 1 18.6,21 H5.4 A1.4,1.4 0 0 1 4,19.6 V4.4 A1.4,1.4 0 0 1 5.4,3 Z M8,7h2M14,7h2M8,11h2M14,11h2M10.5,16v5"
      style={{ fill: 'none', stroke: 'rgb(224,218,212)', strokeWidth: '1.7px' }} />
    {/* evenodd punches the windows/door out of the solid silhouette — without it,
        the active-state fill is one plain rectangle (the bug the original
        network company icon had: its fill path duplicated the outline exactly). */}
    <path className="fill" fillRule="evenodd"
      d="M5.4,3 H18.6 A1.4,1.4 0 0 1 20,4.4 V19.6 A1.4,1.4 0 0 1 18.6,21 H5.4 A1.4,1.4 0 0 1 4,19.6 V4.4 A1.4,1.4 0 0 1 5.4,3 Z M7.3,6.3h1.6v1.6h-1.6z M13.3,6.3h1.6v1.6h-1.6z M7.3,10.3h1.6v1.6h-1.6z M13.3,10.3h1.6v1.6h-1.6z M9.5,16h3v5h-3z"
      style={{ fill: '#222020', stroke: 'none' }} />
  </svg>
)

const timeline = () => (
  <svg className="icon icon-timeline" width="100%" height="100%" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet">
    <path d="M3,12h18" style={{ fill: 'none', stroke: 'rgb(224,218,212)', strokeWidth: '1.7px' }} />
    <path className="fill"
      d="M8,12 A2.4,2.4 0 1 1 3.2,12 A2.4,2.4 0 1 1 8,12 Z M14.4,12 A2.4,2.4 0 1 1 9.6,12 A2.4,2.4 0 1 1 14.4,12 Z M20.8,12 A2.4,2.4 0 1 1 16,12 A2.4,2.4 0 1 1 20.8,12 Z"
      style={{ fill: '#222020', stroke: 'none' }} />
  </svg>
)

// Checkmark badge for "published"-style status counts, as opposed to the plain
// `document` glyph used for a case *list*. Ported here from the old
// clientCases-local IconClientCases (PR #29) when that app was folded into
// `clients` — rebuilt path-only and with the check knocked out of the solid
// disc via evenodd, per this file's two-path convention.
const published = () => (
  <svg className="icon icon-published" width="100%" height="100%" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet">
    <path d="M22,12 A10,10 0 1 1 2,12 A10,10 0 1 1 22,12 M7,12 L10.6,15.6 L17,9.2"
      style={{ fill: 'none', stroke: 'rgb(224,218,212)', strokeWidth: '1.7px' }} />
    <path className="fill" fillRule="evenodd"
      d="M22,12 A10,10 0 1 1 2,12 A10,10 0 1 1 22,12 Z M7,11.4 L10.6,15 L17,8.6 L18.4,10 L10.6,17.8 L5.6,12.8 Z"
      style={{ fill: '#222020', stroke: 'none' }} />
  </svg>
)

// Month grid with a binding — for anything scheduled (recruitment's Intervjuer view). The
// events app keeps its own app-local calendar glyph; this is the shared one for everyone
// else, same evenodd knock-out treatment as `company`/`published` above.
const calendar = () => (
  <svg className="icon icon-calendar" width="100%" height="100%" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet">
    <path d="M4,5h16v16h-16z M4,9.6h16 M8,3v3 M16,3v3"
      style={{ fill: 'none', stroke: 'rgb(224,218,212)', strokeWidth: '1.7px' }} />
    <path className="fill" fillRule="evenodd"
      d="M4,5h16v16h-16z M6.6,11.6h2.2v2.2h-2.2z M10.9,11.6h2.2v2.2h-2.2z M15.2,11.6h2.2v2.2h-2.2z M6.6,15.9h2.2v2.2h-2.2z M10.9,15.9h2.2v2.2h-2.2z M15.2,15.9h2.2v2.2h-2.2z"
      style={{ fill: '#222020', stroke: 'none' }} />
  </svg>
)

const document_ = () => (
  <svg className="icon icon-document" width="100%" height="100%" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet">
    <path d="M6,3h9l4,4v14h-13z M15,3v4h4 M9,11h7M9,14.5h7M9,18h4"
      style={{ fill: 'none', stroke: 'rgb(224,218,212)', strokeWidth: '1.7px' }} />
    <path className="fill" fillRule="evenodd"
      d="M6,3h9l4,4v14h-13z M9,10.2h7v1.6h-7z M9,13.7h7v1.6h-7z M9,17.2h4v1.6h-4z"
      style={{ fill: '#222020', stroke: 'none' }} />
  </svg>
)
